import React, { useState, useEffect } from 'react';
import {Link, useParams} from 'react-router-dom';
import s from './ClosetWishlist.module.css';
import { wardrobeAxiosInstance } from "../../../api";

interface ClothItem {
    id: number;
    name: string;
    manufacturer: string;
    category: string;
    photoUrl: string;
    thumbUrl: string;
    cost: number;
    group: string;
    clothLayer: string;
    elementName: string;
    setId: null | number;
    suit: null | string;
    colorCloth: null | string;
    isFavorite: null | boolean;
    priceSegment: null | string;
}

interface ClothesResponse {
    countRecords: number;
    items: ClothItem[];
}

const ClosetWishlist: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [tab, setTab] = useState<'closet' | 'wishlist'>('closet');
    const [closetItems, setClosetItems] = useState<ClothItem[]>([]);
    const [wishlistItems, setWishlistItems] = useState<ClothItem[]>([]);
    const [itemsLoading, setItemsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(10);
    const [totalCount, setTotalCount] = useState<number>(0);

    useEffect(() => {
        fetchItems();
    }, [tab, currentPage]);

    const fetchItems = async () => {
        setItemsLoading(true);
        setError(null);
        try {
            const endpoint = tab === 'closet'
                ? 'mobile/wardrobe/clothes/list'
                : 'mobile/favorites/clothes';

            const response = await wardrobeAxiosInstance.get<ClothesResponse>(
                endpoint,
                {
                    params: {
                        currentPage: currentPage,
                        itemsPerPage: itemsPerPage,
                        userId: userId
                    }
                }
            );

            if (tab === 'closet') {
                setClosetItems(response.data.items);
            } else {
                setWishlistItems(response.data.items);
            }
            setTotalCount(response.data.countRecords);
        } catch (err) {
            setError('Ошибка при загрузке вещей');
        } finally {
            setItemsLoading(false);
        }
    };

    const handleTabChange = (newTab: 'closet' | 'wishlist') => {
        setTab(newTab);
        setCurrentPage(1);
        setError(null);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const currentItems = tab === 'closet' ? closetItems : wishlistItems;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
        <div className={s.container}>
            <div className={s.header}>
                <h2 className={s.title}>Шкаф и вишлист</h2>
                <span className={s.count}>Всего: {totalCount}</span>
            </div>

            <div className={s.tabs}>
                <button
                    className={`${s.tab} ${tab === 'closet' ? s.active : ''}`}
                    onClick={() => handleTabChange('closet')}
                >
                    Шкаф
                </button>
                <button
                    className={`${s.tab} ${tab === 'wishlist' ? s.active : ''}`}
                    onClick={() => handleTabChange('wishlist')}
                >
                    Вишлист
                </button>
            </div>

            <div className={s.gridWrapper}>
                {error ? (
                    <div className={s.error}>{error}</div>
                ) : itemsLoading ? (
                    <div className={s.gridLoader}>
                        <div className={s.spinner} />
                        <p>Загрузка вещей...</p>
                    </div>
                ) : currentItems.length === 0 ? (
                    <p className={s.empty}>
                        {tab === 'closet' ? 'В шкафу пока нет вещей' : 'В вишлисте пока нет вещей'}
                    </p>
                ) : (
                    <>
                        <div className={s.grid}>
                            {currentItems.map((item) => (
                                <div key={item.id} className={s.itemCard}>
                                    <Link
                                        to={`/details/${item.id}`}
                                        target="_blank"
                                        className={s.cardLink}
                                    >
                                        <div className={s.imageWrapper}>
                                            <img
                                                src={item.thumbUrl || item.photoUrl || '/placeholder.svg'}
                                                alt={item.name}
                                            />
                                            {tab === 'wishlist' && (
                                                <span className={s.wishlistBadge}>♥</span>
                                            )}
                                        </div>
                                        <div className={s.itemInfo}>
                                            <p className={s.itemName}>{item.name}</p>
                                            <p className={s.itemBrand}>{item.manufacturer}</p>
                                            <p className={s.itemPrice}>{item.cost} ₽</p>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className={s.pagination}>
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={s.pageButton}
                                >
                                    ← Назад
                                </button>
                                <span className={s.pageInfo}>
                                    Страница {currentPage} из {totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={s.pageButton}
                                >
                                    Вперед →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ClosetWishlist;